import { cn } from '@/shared/utils/cn';

export type TabsItem<T extends string> = {
  value: T;
  label: string;
};

type Props<T extends string> = {
  items: readonly TabsItem<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
};

/**
 * Lightweight segmented tab strip. Bottom-border highlight on active, slate
 * text on inactive. Used by the Omborxona page above the filter row. Kept
 * unopinionated about positioning — caller controls horizontal alignment.
 */
export const Tabs = <T extends string>({ items, value, onChange, className }: Props<T>) => (
  <div
    role="tablist"
    className={cn(
      'flex items-center gap-1 border-b border-slate-200',
      className,
    )}
  >
    {items.map((item) => {
      const active = item.value === value;
      return (
        <button
          key={item.value}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(item.value)}
          className={cn(
            '-mb-px cursor-pointer border-b-2 px-3 py-2 text-sm font-medium transition-colors',
            active
              ? 'border-brand-600 text-brand-700'
              : 'border-transparent text-slate-500 hover:text-slate-700',
          )}
        >
          {item.label}
        </button>
      );
    })}
  </div>
);
